import {lazy, memo, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useDecision} from '@s-ui/pde'
import {atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import MoleculeSelectPopover, {selectPopoverOverlayTypes} from '@s-ui/react-molecule-select-popover'
import PolymorphicElement from '@s-ui/react-primitive-polymorphic-element'
import VisuallyHidden from '@s-ui/react-primitive-visually-hidden'
import {FormVersionBuilder} from '@adv-mt/mt-form-version'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import ChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import ChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'

import FormVehiclesLabel from './components/Label/index.js'
import ModalContent from './components/ModalContent/index.js'
import FormVehiclesModeToggle from './components/ModeToggle/index.js'
import {BASE_CLASS, BASE_ELEMENTS, DESIGNS, MODES} from './config.js'
import {canExcludeVehicles, getExcludableOptions, getVehiclesLabel, removeConflictingExclusions} from './utils.js'

const NO_EXCLUDED_VEHICLES = []

const FormVehiclesMakeModel = lazy(() =>
  import(/* webpackChunkName: "LazyFormVehicles" */ './components/MakeModel/index.js')
)

const FormVehicles = ({
  aggregations,
  allowExcludeFilter = false,
  design,
  initialSearch,
  isLoading,
  onChange,
  options,
  overlayContentRef,
  pageName,
  scrollIntoView = false,
  showVersion = true,
  withLabel = true,
  value
}) => {
  const [hasOpenedMakeModelFilter, setHasOpenedMakeModelFilter] = useState(false)

  useEffect(() => {
    setHasOpenedMakeModelFilter(true)
  }, [])

  const {enabled: isExcludeFilterEnabled} = useDecision('ff_web_buyer_exclude_make_model_filter', {
    attributes: {hasOpenedMakeModelFilter: allowExcludeFilter && hasOpenedMakeModelFilter}
  })
  const [tempSelections, setTempSelections] = useState(value)
  const [tempExcluded, setTempExcluded] = useState(initialSearch?.excludedVehicles ?? NO_EXCLUDED_VEHICLES)
  const [mode, setMode] = useState(MODES.ADD)
  const [forceClosePopover, setForceClosePopover] = useState(false)

  const delayToNextFrame = useDelayToNextFrame()

  const {domain, i18n} = useContext(Context)

  const popoverRef = useRef()
  const popoverContentRef = useRef()
  // Latest selections, so consecutive changes in the same frame never start from stale state
  const tempSelectionsRef = useRef(tempSelections)
  const tempExcludedRef = useRef(tempExcluded)
  tempSelectionsRef.current = tempSelections
  tempExcludedRef.current = tempExcluded

  const config = domain.get('config')
  const {DEFAULT_MODEL, DEFAULT_VEHICLE, FILTER_NAMES, DEFAULT_FILTERS_LABELS} = config.get('search')
  const excludedVehicles = initialSearch?.[FILTER_NAMES.EXCLUDED_VEHICLES] ?? NO_EXCLUDED_VEHICLES
  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')

  const isModalContentDesign = design === DESIGNS.MODAL
  const isRightPopoverDesign = design === DESIGNS.RIGHT_POPOVER

  const baseClassNames = cx(`${BASE_CLASS}`, design && `${BASE_CLASS}--${design}`)

  useEffect(() => {
    setTempSelections(value)
  }, [value])

  useEffect(() => {
    setTempExcluded(excludedVehicles)
  }, [excludedVehicles])

  const canExclude =
    isExcludeFilterEnabled && canExcludeVehicles({defaultModelId: DEFAULT_MODEL.id, vehicles: tempSelections})
  const isExcludeMode = canExclude && mode === MODES.EXCLUDE

  const excludableOptions = useMemo(
    () =>
      isExcludeMode
        ? getExcludableOptions({
            defaultModelId: DEFAULT_MODEL.id,
            excludedVehicles: tempExcluded,
            options,
            vehicles: tempSelections.filter(({makeId}) => makeId)
          })
        : options,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isExcludeMode, options, tempExcluded, tempSelections]
  )

  // The excluded vehicles travel with the vehicles so both are applied in one search update
  const getExcludedSearch = excluded =>
    isExcludeFilterEnabled ? {extraSearch: {[FILTER_NAMES.EXCLUDED_VEHICLES]: excluded}} : {}

  const handleSelectVehicles = useCallback(
    ({makeId: selectedMakeId, vehicles: newVehicles}) => {
      const vehicles = [
        ...tempSelectionsRef.current.filter(({makeId}) => String(makeId) !== String(selectedMakeId)),
        ...newVehicles
      ]
      const excluded = isExcludeFilterEnabled
        ? removeConflictingExclusions({
            defaultModelId: DEFAULT_MODEL.id,
            excludedVehicles: tempExcludedRef.current,
            makeId: selectedMakeId,
            newVehicles
          })
        : tempExcludedRef.current

      tempSelectionsRef.current = vehicles
      tempExcludedRef.current = excluded
      setTempSelections(vehicles)
      setTempExcluded(excluded)

      if (!isModalContentDesign) {
        onChange(null, {
          field: FILTER_NAMES.VEHICLES,
          value: vehicles,
          lastFilter: TRACKING_FILTER_NAMES[FILTER_NAMES.VEHICLES],
          withFilterAddedEvent: true,
          ...getExcludedSearch(excluded)
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, isExcludeFilterEnabled]
  )

  const handleSelectExcludedVehicles = useCallback(
    ({makeId: selectedMakeId, vehicles: newVehicles}) => {
      const excluded = [
        ...tempExcludedRef.current.filter(({makeId}) => String(makeId) !== String(selectedMakeId)),
        ...newVehicles
      ]

      tempExcludedRef.current = excluded
      setTempExcluded(excluded)

      if (!isModalContentDesign) {
        onChange(null, {
          field: FILTER_NAMES.VEHICLES,
          value: tempSelectionsRef.current,
          lastFilter: FILTER_NAMES.EXCLUDED_VEHICLES,
          withFilterAddedEvent: true,
          ...getExcludedSearch(excluded)
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, isExcludeFilterEnabled]
  )

  const handleChangeVersion = useCallback(
    (_, data) => {
      setTempSelections(prevState => {
        const vehicles = prevState.length
          ? prevState.map(vehicle => ({
              ...vehicle,
              version: data.value
            }))
          : [{...DEFAULT_VEHICLE, make: '', version: data.value}]

        onChange(null, {
          field: FILTER_NAMES.VEHICLES,
          value: vehicles,
          lastFilter: TRACKING_FILTER_NAMES[FILTER_NAMES.VEHICLES],
          withFilterAddedEvent: true
        })
        return vehicles
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange]
  )

  const handleCancel = () => {
    setTempSelections(value)
    setTempExcluded(excludedVehicles)
  }

  const handleAccept = () => {
    if (isModalContentDesign) {
      setForceClosePopover(true)
      delayToNextFrame(() => {
        onChange(null, {
          field: FILTER_NAMES.VEHICLES,
          value: tempSelections,
          lastFilter: isExcludeMode ? FILTER_NAMES.EXCLUDED_VEHICLES : TRACKING_FILTER_NAMES[FILTER_NAMES.VEHICLES],
          ...getExcludedSearch(tempExcluded)
        })
      })
    }
  }

  const handleRemoveVehicles = () => {
    const vehicles = isExcludeMode ? tempSelections : []
    const excluded = isExcludeMode ? [] : tempExcluded

    setTempSelections(vehicles)
    setTempExcluded(excluded)
    delayToNextFrame(() => {
      onChange(null, {
        field: FILTER_NAMES.VEHICLES,
        value: vehicles,
        ...(!isExcludeMode && {lastFilter: TRACKING_FILTER_NAMES[FILTER_NAMES.VEHICLES]}),
        ...getExcludedSearch(excluded)
      })
    })
  }

  const label = i18n.t(DEFAULT_FILTERS_LABELS[FILTER_NAMES.VEHICLES])
  const getPlaceholder = () => {
    if (isExcludeMode) return i18n.t('COMMON.NONE_FEM')
    if (isExcludeFilterEnabled) return i18n.t('COMMON.ALL_FEM')
    return withLabel ? i18n.t('COMMON.ALL_MAKES_MODELS') : label
  }
  const placeholder = getPlaceholder()

  const hasSelections = Boolean(isExcludeMode ? tempExcluded.length : tempSelections.length)

  const selectionsLabels = isExcludeMode ? getVehiclesLabel(tempExcluded) : getVehiclesLabel(tempSelections)

  const selectText = selectionsLabels || placeholder

  const handleOpen = () => {
    scrollIntoView && popoverContentRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
  }

  const handleClose = () => {
    scrollIntoView && popoverRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
  }

  return design === DESIGNS.BASIC ? (
    <Suspense fallback={<div style={{height: '400px'}} />}>
      <FormVehiclesMakeModel
        aggregations={aggregations}
        design={design}
        onChange={handleSelectVehicles}
        options={options}
        pageName={pageName}
        value={tempSelections}
      />
    </Suspense>
  ) : (
    <>
      <PolymorphicElement
        as={showVersion ? BASE_ELEMENTS.FIELDSET : BASE_ELEMENTS.DIV}
        className={baseClassNames}
        ref={popoverRef}
      >
        {showVersion ? (
          <>
            <VisuallyHidden as={BASE_ELEMENTS.LEGEND}>{label}</VisuallyHidden>
            <FormVehiclesLabel hidden={!withLabel} id={FILTER_NAMES.VEHICLES} label={label} />
          </>
        ) : (
          <FormVehiclesLabel hidden={!withLabel} id={FILTER_NAMES.VEHICLES} label={label} />
        )}
        {isExcludeFilterEnabled && (
          <FormVehiclesModeToggle
            isExcludeDisabled={!canExclude}
            mode={isExcludeMode ? MODES.EXCLUDE : MODES.ADD}
            onChange={setMode}
          />
        )}
        <MoleculeSelectPopover
          acceptButtonOptions={{
            className: `${BASE_CLASS}-modalFooterActionsAccept`,
            isLoading,
            shape: atomButtonShapes.CIRCULAR
          }}
          acceptButtonText={i18n.t('COMMON.ACCEPT')}
          cancelButtonOptions={{shape: atomButtonShapes.CIRCULAR}}
          cancelButtonText={i18n.t('COMMON.CANCEL')}
          forceClosePopover={forceClosePopover}
          fullWidth
          iconArrowDown={isRightPopoverDesign ? ChevronRight : ChevronDown}
          id={FILTER_NAMES.VEHICLES}
          isSelected={hasSelections}
          onAccept={handleAccept}
          onCancel={handleCancel}
          onClose={handleClose}
          onOpen={handleOpen}
          overlayContentRef={overlayContentRef}
          overlayType={selectPopoverOverlayTypes.LIGHT}
          renderContentWrapper={isModalContentDesign && ModalContent}
          removeButtonOptions={{
            isShown: hasSelections,
            onClick: handleRemoveVehicles,
            rightIcon: IconClose,
            shape: atomButtonShapes.SQUARED
          }}
          selectText={selectText}
        >
          <Suspense fallback={<div style={{height: '400px'}} ref={popoverContentRef} />}>
            <FormVehiclesMakeModel
              key={isExcludeMode ? MODES.EXCLUDE : MODES.ADD}
              aggregations={aggregations}
              design={design}
              options={isExcludeMode ? excludableOptions : options}
              onChange={isExcludeMode ? handleSelectExcludedVehicles : handleSelectVehicles}
              pageName={pageName}
              ref={popoverContentRef}
              value={isExcludeMode ? tempExcluded : tempSelections}
            />
          </Suspense>
        </MoleculeSelectPopover>
        {showVersion && !isExcludeMode && (
          <FormVersionBuilder
            initialSearch={{categoryId: initialSearch.categoryId}}
            onChange={handleChangeVersion}
            value={initialSearch.version}
          />
        )}
      </PolymorphicElement>
    </>
  )
}

FormVehicles.propTypes = {
  aggregations: PropTypes.array,
  allowExcludeFilter: PropTypes.bool,
  design: PropTypes.oneOf(Object.values(DESIGNS)),
  initialSearch: PropTypes.object,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func,
  options: PropTypes.array,
  overlayContentRef: PropTypes.node,
  pageName: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  showVersion: PropTypes.bool,
  withLabel: PropTypes.bool,
  value: PropTypes.string
}

FormVehicles.displayName = 'FormVehicles'

const FormVehiclesBasicContent = props => {
  return <FormVehicles {...props} design={DESIGNS.BASIC} />
}

FormVehiclesBasicContent.displayName = 'FormVehiclesBasicContent'

const FormVehiclesModalSelectPopover = props => {
  return <FormVehicles {...props} design={DESIGNS.MODAL} />
}

FormVehiclesModalSelectPopover.displayName = 'FormVehiclesModalSelectPopover'

const FormVehiclesRightSelectPopover = props => {
  return <FormVehicles {...props} design={DESIGNS.RIGHT_POPOVER} />
}

FormVehiclesRightSelectPopover.displayName = 'FormVehiclesRightSelectPopover'

const FormVehiclesCenterToCustomRelativePopover = props => {
  return <FormVehicles {...props} design={DESIGNS.CENTER_TO_CUSTOM_RELATIVE_POPOVER} />
}

FormVehiclesCenterToCustomRelativePopover.displayName = 'FormVehiclesCenterToCustomRelativePopover'

const FormVehiclesCenterToCustomRelativePopoverWithoutVersion = props => {
  return (
    <FormVehicles {...props} design={DESIGNS.CENTER_TO_CUSTOM_RELATIVE_POPOVER} scrollIntoView showVersion={false} />
  )
}

FormVehiclesCenterToCustomRelativePopoverWithoutVersion.displayName =
  'FormVehiclesCenterToCustomRelativePopoverWithoutVersion'

const MemoFormVehiclesBasicContent = memo(FormVehiclesBasicContent)
const MemoFormVehiclesModalSelectPopover = memo(FormVehiclesModalSelectPopover)
const MemoFormVehiclesRightSelectPopover = memo(FormVehiclesRightSelectPopover)
const MemoFormVehiclesCenterToCustomRelativePopover = memo(FormVehiclesCenterToCustomRelativePopover)

export {
  MemoFormVehiclesBasicContent as FormVehiclesBasicContent,
  MemoFormVehiclesModalSelectPopover as FormVehiclesModalSelectPopover,
  MemoFormVehiclesRightSelectPopover as FormVehiclesRightSelectPopover,
  MemoFormVehiclesCenterToCustomRelativePopover as FormVehiclesCenterToCustomRelativePopover,
  FormVehiclesCenterToCustomRelativePopoverWithoutVersion,
  DESIGNS as formVehiclesDesigns
}

export default MemoFormVehiclesBasicContent
